import {useState} from 'react';
import {useLocation} from 'react-router-dom';
import AddPlaceSheet from './AddPlaceSheet';
import Icon from '../Icon';
import {usePlaces} from '../../api/places';
import {groupPlaces} from '../../lib/placeTree';

// "Where" choice chips (DESIGN.md §5): areas and their spots as 40px pills; the selected one in
// shoot-soft with a check; "+ New" makes a place right here in a sheet and selects it. An area
// chip means "somewhere in that area".
type Props = {value: string | null; onChange: (placeId: string | null) => void};

export default function WhereChips({value, onChange}: Props) {
    const places = usePlaces(false);
    const {pathname} = useLocation();
    const [adding, setAdding] = useState(false);
    const all = places.data ?? [];
    const groups = groupPlaces(all);
    const selected = all.find(p => p.id === value);
    const suggestedAreaId = selected ? (selected.parentId ?? selected.id) : null;

    const chip = (id: string, label: string, muted = false) => {
        const isSelected = value === id;
        return (
            <button
                key={id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onChange(isSelected ? null : id)}
                className={`inline-flex h-10 items-center gap-[6px] rounded-pill px-[14px] text-sm ${
                    isSelected ? 'border-[1.5px] border-shoot bg-shoot-soft pl-[10px] font-bold text-shoot-text' : `border border-line bg-surface font-semibold ${muted ? 'text-ink-2' : ''}`
                }`}
            >
                {isSelected && <Icon name="check" size={16} strokeWidth={2.2} />}
                {label}
            </button>
        );
    };

    return (
        <div className="flex flex-col gap-1">
            <span className="band-label text-ink-2">Where</span>
            <div className="flex flex-wrap gap-2">
                {groups.map(({area, spots}) => [chip(area.id, spots.length > 0 ? `${area.name} · anywhere` : area.name, spots.length > 0), ...spots.map(s => chip(s.id, s.name))])}
                <button type="button" onClick={() => setAdding(true)} className="inline-flex h-10 items-center gap-1 rounded-pill border-[1.5px] border-dashed border-line px-[14px] text-sm font-semibold text-ink-2">
                    <Icon name="plus" size={16} /> New
                </button>
            </div>
            {adding && (
                <AddPlaceSheet
                    open
                    onClose={() => setAdding(false)}
                    places={all}
                    suggestedAreaId={suggestedAreaId}
                    returnTo={pathname}
                    onCreated={place => onChange(place.id)}
                />
            )}
        </div>
    );
}
